import {
  registerCellType,
  NumericCellType,
  DropdownCellType,
  DateCellType,
  CheckboxCellType,
  TextCellType,
} from 'handsontable/cellTypes';

import {
  registerPlugin,
  CopyPaste,
  ContextMenu,
  AutoColumnSize,
  Filters,
  Autofill,
  DropdownMenu,
  HiddenRows,
  HiddenColumns,
} from 'handsontable/plugins';

// Custom editors
import { ColorEditor } from '@/components/UI/SpreadSheet/editors/ColorEditor';
import { ExponentialEditor } from '@/components/UI/SpreadSheet/editors/ExponentialEditor';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { readMultiSelectCell } from '@/components/UI/SpreadSheet/multiSelectCellTransport';
import { DateEditorWrapper } from '@/components/UI/SpreadSheet/editors/DateEditorWrapper';
import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import { ScoringScaleEditor } from '@/components/UI/SpreadSheet/editors/ScoringScaleEditor';
import { TimepointEditor } from '@/components/UI/SpreadSheet/editors/TimepointEditor';

// Custom validators
import { registerCustomValidators } from './hot.validators';

// Custom renderers
import { registerCustomRenderers } from './hot.renderers';

import Handsontable from 'handsontable';
import type {
  MetadataField,
  MetadataFieldWithValue,
  MetadataFieldValueTypes,
  MetadataFieldTypes,
} from '@/model/Metadata.model';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { numericValidatorFactory, textValidatorFactory, createHotRequiredValidator } from '@/validators';
import { formatNumberComingIn } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';

let isRegistered = false;

/**
 * Register all Handsontable modules
 *
 * This function should be called once before using any Handsontable components.
 * It's safe to call multiple times - registration will only happen once.
 */
export const registerHOTModules = (): void => {
  if (isRegistered) {
    return;
  }

  // Register custom editors
  ColorEditor.register();
  ExponentialEditor.register();
  MultiSelectEditor.register();
  DateEditorWrapper.register();
  DateTimeEditor.register();
  ScoringScaleEditor.register();
  TimepointEditor.register();

  // Register cell types
  registerCellType(TextCellType);
  registerCellType(NumericCellType);
  registerCellType(DropdownCellType);
  registerCellType(DateCellType);
  registerCellType(CheckboxCellType);

  // Register plugins
  registerPlugin(CopyPaste);
  registerPlugin(ContextMenu);
  registerPlugin(AutoColumnSize);
  registerPlugin(Filters);
  registerPlugin(Autofill);
  registerPlugin(DropdownMenu);
  registerPlugin(HiddenRows);
  registerPlugin(HiddenColumns);

  // Register custom validators
  registerCustomValidators(Handsontable);

  // Register custom renderers
  registerCustomRenderers(Handsontable);

  isRegistered = true;
};

/**
 * Reset registration state (useful for testing)
 */
export const resetRegistration = (): void => {
  isRegistered = false;
};

/**
 * Check if modules are registered
 */
export const isHOTModulesRegistered = (): boolean => isRegistered;

// Export row header renderer
export { createRowHeaderWithControlsRenderer, rowHeaderWithControlsRenderer } from './hot.rowHeaderWithControls';

// Export validator and renderer names
export { validators } from './hot.validators';
export { renderers } from './hot.renderers';

/**
 * Format metadata title for use as column data accessor
 */
export const formatColumnSettingsDataValue = (str: string): string =>
  encodeURIComponent(str).replace(/[.]+/g, '%2E').toLowerCase();

/**
 * Default metadata to column settings mapper for Handsontable
 *
 * Maps metadata fields to Handsontable column settings with appropriate
 * editors, validators, and renderers based on field type.
 *
 * NOTE: Currently uses basic types until custom editors are created
 */
export const defaultMetadataToColumnSettingMapper = (metadata: MetadataField) => {
  const data = `meta.${formatColumnSettingsDataValue(metadata.title)}`;
  const metaID = metadata.id;
  const isRequired = metadata.required ?? false;

  switch (metadata.field_type) {
    case 'select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: MultiSelectEditor.type,
          isMultiSelect: false,
          isClearable: true,
          options: metadata.options,
          width: 150,
          validator: createHotRequiredValidator(
            metadata.title,
            isRequired,
            MultiSelectEditor.validatorFactory(metadata.options ?? [], false)
          ),
        },
      };
    }
    case 'multi_select': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: MultiSelectEditor.type,
          isMultiSelect: true,
          isClearable: true,
          jsonEncoded: true,
          options: metadata.options,
          width: 150,
          validator: createHotRequiredValidator(
            metadata.title,
            isRequired,
            MultiSelectEditor.validatorFactory(metadata.options ?? [], true, isRequired ? 1 : 0, Infinity, true)
          ),
        },
      };
    }
    case 'text': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: 'text',
          metadataType: 'text',
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, textValidatorFactory()),
          validationErrorMessage: (value: unknown) => {
            const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
            if (stringValue.length > 255) {
              return `Must have a max length of 255 characters`;
            }
            return `Is required`;
          },
        },
      };
    }
    case 'date': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: DateEditorWrapper.type,
          dateFormat: DateInputUtils.hotDatePattern,
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, DateUtils.validatorWithCallback()),
        },
      };
    }
    case 'numeric': {
      return {
        columnHeader: metadata.title,
        columnSettings: {
          metaID,
          data,
          type: ExponentialEditor.type,
          width: 150,
          validator: createHotRequiredValidator(metadata.title, isRequired, numericValidatorFactory()),
        },
      };
    }
    default: {
      throw new Error(`Unrecognised metadata field_type [${metadata.field_type}]`);
    }
  }
};

/**
 * Shape of a single metadata entry produced for an API payload (equivalent of the
 * v12 `HoTMeta` interface that lived in `@/support/hot`).
 */
export interface HoTMeta {
  value: MetadataFieldValueTypes;
  glossary_id: string;
}

export interface CreateMetaOptions {
  /**
   * Emit an explicit `null` for cells the user cleared. Update endpoints read an omitted glossary
   * key as "leave untouched" and a `null` value as "clear the stored value", so only they opt in;
   * create endpoints have nothing to clear and keep omitting empty cells.
   */
  emitClearedValues?: boolean;
}

/**
 *  version of createMeta — converts spreadsheet row metadata into API payload format.
 *
 * Differences from v12 createMeta:
 *  - Editor type names use suffixes (multi-select, numeric-expo)
 *  - Column data keys are already encoded (via formatColumnSettingsDataValue) at column definition time
 */
export const createMeta = (
  meta: Record<string, MetadataFieldValueTypes | undefined>,
  columns: Array<{ data?: string | number | object; type?: string; metaID?: string | number; isMultiSelect?: boolean }>,
  { emitClearedValues = false }: CreateMetaOptions = {}
): Array<{ value: MetadataFieldValueTypes; glossary_id: string | number }> =>
  Object.entries(meta).reduce<Array<{ value: MetadataFieldValueTypes; glossary_id: string | number }>>(
    (acc, [title, value]) => {
      const cellData = columns.find((c) => c.data === `meta.${title}`);
      if (_isNil(cellData) || _isNil(cellData.metaID)) {
        return acc;
      }

      const { type, metaID, isMultiSelect } = cellData;

      // An untouched cell arrives as `undefined` and stays out of the payload, whereas '' and null
      // are cells the user emptied and are only reported when the caller can act on a clear.
      if (value === undefined) {
        return acc;
      }
      if (_isNil(value) || value === '') {
        if (emitClearedValues) {
          acc.push({ value: null, glossary_id: metaID });
        }
        return acc;
      }

      let newValue: MetadataFieldValueTypes;
      if (type === MultiSelectEditor.type && typeof value === 'string' && isMultiSelect !== false) {
        newValue = readMultiSelectCell(value);
      } else if (type === ExponentialEditor.type) {
        newValue = formatNumberComingIn(value);
      } else {
        newValue = String(value);
      }

      acc.push({ value: newValue, glossary_id: metaID });
      return acc;
    },
    []
  );

/**
 * Check if a metadata value is set (not null, undefined, or empty)
 */
export const valueSet = (value: MetadataFieldValueTypes): boolean => {
  return (
    _notNil(value) &&
    ((typeof value === 'string' && value.trim().length > 0) ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      (Array.isArray(value) && value.length > 0))
  );
};

/**
 * Sanitize metadata values based on field type
 */
export const sanitizeMetadataValue = (
  value: MetadataFieldValueTypes,
  type: MetadataFieldTypes
): MetadataFieldValueTypes => {
  if (Array.isArray(value)) {
    return value.filter(valueSet);
  }
  if (type === 'numeric' && _notNil(value)) {
    return !isNaN(formatNumberComingIn(value)) ? value : '';
  }

  return value;
};

/**
 * Sorts a metadata map [[glossaryId]: Metadata] to Array<Metadata>
 *
 * @description
 * - Additional metadata added will be placed at the end
 * - Metadata values will be sanitized (ie nil / empty values removed)
 *
 * @param metadataGlossaryIdOrder Array of glossary IDs in desired order
 * @param metadataIdsToRetain Set of metadata IDs to include in output
 * @param metadata Record of metadata by glossary ID
 * @returns Sorted array of metadata with values
 */
export const metadataMapToSortedMetadataArray = (
  metadataGlossaryIdOrder: Array<number>,
  metadataIdsToRetain: Set<ID>,
  metadata?: Record<string, MetadataFieldWithValue>
): Array<MetadataFieldWithValue> => {
  const md = { ...metadata };
  const metadataArray = metadataGlossaryIdOrder.reduce<Array<MetadataFieldWithValue>>((acc, glossaryId) => {
    if (_notNil(md?.[glossaryId])) {
      const metadataItem = md[glossaryId];
      delete md[glossaryId];
      return [...acc, metadataItem];
    }
    return acc;
  }, []);
  metadataArray.push(...Object.values(md)); // add unsorted metadata to the end
  return metadataArray.reduce<Array<MetadataFieldWithValue>>((acc, metadataItem) => {
    delete metadataItem['options'];
    if (
      metadataIdsToRetain.has(metadataItem.glossary_id) &&
      valueSet(sanitizeMetadataValue(metadataItem.value, metadataItem.field_type))
    ) {
      return [
        ...acc,
        {
          ...metadataItem,
          value: sanitizeMetadataValue(metadataItem.value, metadataItem.field_type),
        },
      ];
    }
    return acc;
  }, []);
};

/**
 * Re-export Handsontable for convenience
 */
export { Handsontable as Handsontable };
